import { useLayoutEffect, useRef, useState, type RefObject } from 'react'

const DEFAULT_ROW_HEIGHT = 52
export const FIT_PAGE_ROW_ATTR = 'data-fit-page-row'

export function cssRowHeight(): number {
  if (typeof document === 'undefined') return DEFAULT_ROW_HEIGHT
  const raw = getComputedStyle(document.documentElement).getPropertyValue('--row-height').trim()
  const parsed = Number.parseFloat(raw)
  return !Number.isNaN(parsed) && parsed > 0 ? parsed : DEFAULT_ROW_HEIGHT
}

type FitVisibleRowCountOptions = {
  /**
   * Use CSS `--row-height` instead of measuring the first row.
   * Prefer for tables whose rows grow with wrapped content.
   */
  useCssRowHeight?: boolean
}

/** How many rows fit in the container. Null if layout is not ready (do not persist). */
export function fitVisibleRowCount(
  container: HTMLElement,
  rowSelector = `[${FIT_PAGE_ROW_ATTR}]`,
  options: FitVisibleRowCountOptions = {}
): number | null {
  if (!container.isConnected) return null
  const row = container.querySelector(rowSelector) as HTMLElement | null
  const rowHeight = options.useCssRowHeight
    ? cssRowHeight()
    : row?.getBoundingClientRect().height || cssRowHeight()
  if (rowHeight <= 0 || container.clientHeight < rowHeight) return null
  return Math.max(1, Math.floor(container.clientHeight / rowHeight))
}

type UseFitPageSizeOptions = {
  enabled?: boolean
  rowSelector?: string
  /** Re-bind observer when list content appears (actual row height vs CSS fallback). */
  layoutKey?: string | number
  page?: number
  onPageChange?: (page: number) => void
  /** Capacity from CSS row height; ignore measured (possibly wrapped) first row. */
  useCssRowHeight?: boolean
}

/**
 * Page size from container height. Ignores zero-height (unmounted / not laid out)
 * so leaving a list does not collapse the page to one row.
 */
export function useFitPageSize(
  containerRef: RefObject<HTMLElement | null>,
  options: UseFitPageSizeOptions = {}
): number {
  const { enabled = true, rowSelector, layoutKey, page = 1, onPageChange, useCssRowHeight } = options
  const [pageSize, setPageSize] = useState(0)
  const pageSizeRef = useRef(0)
  const pageRef = useRef(page)
  const onPageChangeRef = useRef(onPageChange)
  pageSizeRef.current = pageSize
  pageRef.current = page
  onPageChangeRef.current = onPageChange

  useLayoutEffect(() => {
    if (!enabled) return undefined
    const container = containerRef.current
    if (!container) return undefined

    const recalculate = () => {
      const next = fitVisibleRowCount(container, rowSelector, { useCssRowHeight })
      if (next == null || next === pageSizeRef.current) return
      const previous = pageSizeRef.current
      if (previous > 0 && onPageChangeRef.current) {
        const firstIndex = (pageRef.current - 1) * previous
        const nextPage = Math.floor(firstIndex / next) + 1
        if (nextPage !== pageRef.current) onPageChangeRef.current(Math.max(1, nextPage))
      }
      setPageSize(next)
    }

    recalculate()
    const observer = new ResizeObserver(recalculate)
    observer.observe(container)
    window.addEventListener('resize', recalculate)
    return () => {
      observer.disconnect()
      window.removeEventListener('resize', recalculate)
    }
  }, [containerRef, enabled, layoutKey, rowSelector, useCssRowHeight])

  return pageSize
}
